import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Pagination,
  Select,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { IconCheck, IconShieldCheck, IconShieldX } from "@/lib/icons";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { PageHeader } from "@/components/PageHeader";
import { formatDateTime } from "@/lib/format";
import { checkAuditIntegrity, listAudit } from "@/lib/api/settings";

const VERB_COLORS: Record<string, string> = {
  wrote: "brand",
  read: "gray",
  failed: "red",
};

const ENTITY_KEYS = new Set([
  "Patient",
  "Examination",
  "ExaminationSection",
  "User",
  "Form",
  "LabOrder",
  "LabResult",
  "FunctionalTest",
  "Backup",
  "Clinic",
  "Mkb10Code",
  "LabTestDefinition",
  "Organization",
  "ExaminationType",
  "Medication",
  "Deputy",
  "SalaryPayment",
]);

export function AuditLogPage() {
  const { t } = useTranslation();
  const [pageSize, setPageSize] = useState<number>(50);
  const [page, setPage] = useState<number>(1);

  const { data, isLoading } = useQuery({
    queryKey: ["audit", page, pageSize],
    queryFn: () =>
      listAudit({ limit: pageSize, offset: (page - 1) * pageSize }),
  });

  const integrity = useMutation({
    mutationFn: checkAuditIntegrity,
  });

  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / pageSize));

  const verbLabel = (verb: string) =>
    t(`audit.verbs.${verb}`, { defaultValue: verb });

  const permissionLabel = (perm: string) => {
    // try translated key, fallback to formatted version
    const tr = t(`audit.permissions.${perm}`, { defaultValue: "" });
    return tr || perm;
  };

  const targetLabel = (target: string | null | undefined) => {
    if (!target) return "—";
    if (ENTITY_KEYS.has(target)) {
      return t(`audit.entities.${target}`, { defaultValue: target });
    }
    return target;
  };

  const roleLabel = (role: string | null | undefined) => {
    if (!role) return "—";
    return t(`users.roles.${role}`, { defaultValue: role });
  };

  const diffFieldLabel = (key: string) =>
    t(`audit.diff_fields.${key}`, { defaultValue: key });

  return (
    <Stack gap="lg">
      <PageHeader
        title={t("audit.title")}
        subtitle={data ? `${data.total}` : undefined}
        back
        actions={
          <Button
            leftSection={<IconShieldCheck size={16} strokeWidth={2.2} />}
            variant="light"
            radius="xl"
            onClick={() => integrity.mutate()}
            loading={integrity.isPending}
          >
            {t("audit.check_integrity")}
          </Button>
        }
      />

      {integrity.data && (
        <Alert
          color={integrity.data.ok ? "blue" : "red"}
          icon={integrity.data.ok ? <IconCheck size={16} /> : <IconShieldX size={16} />}
          radius="lg"
        >
          {integrity.data.ok
            ? t("audit.chain_ok", { count: integrity.data.rows_checked })
            : t("audit.chain_broken", { index: integrity.data.broken_at_index })}
        </Alert>
      )}

      <Card padding="md" radius="xl">
        <Group justify="space-between" wrap="wrap">
          <Group gap="sm">
            <Text size="sm" c="dimmed">
              {t("audit.page_size")}:
            </Text>
            <Select
              size="xs"
              w={90}
              data={["25", "50", "100", "200"]}
              value={String(pageSize)}
              onChange={(v) => {
                if (v) {
                  setPageSize(Number(v));
                  setPage(1);
                }
              }}
            />
          </Group>
          <Pagination
            total={totalPages}
            value={page}
            onChange={setPage}
            size="sm"
            radius="md"
          />
        </Group>
      </Card>

      <Card padding={0} radius="xl">
        <Table.ScrollContainer minWidth={920}>
          <Table verticalSpacing="md" striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("audit.fields.created_at")}</Table.Th>
                <Table.Th>{t("audit.fields.actor_role")}</Table.Th>
                <Table.Th>{t("audit.fields.permission")}</Table.Th>
                <Table.Th>{t("audit.fields.verb")}</Table.Th>
                <Table.Th>{t("audit.fields.target")}</Table.Th>
                <Table.Th>{t("audit.fields.diff")}</Table.Th>
                <Table.Th>{t("audit.fields.ip")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {isLoading && (
                <Table.Tr>
                  <Table.Td colSpan={7}>
                    <Text c="dimmed" ta="center" py="md">
                      {t("common.loading")}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
              {!isLoading && (data?.items.length ?? 0) === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={7}>
                    <Text c="dimmed" ta="center" py="md">
                      {t("audit.empty")}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
              {data?.items.map((row) => (
                <Table.Tr key={row.id}>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {formatDateTime(row.created_at)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {roleLabel(row.actor_role)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {permissionLabel(row.permission)}
                    </Text>
                    <Text ff="monospace" size="xs" c="dimmed">
                      {row.permission}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      variant="light"
                      size="sm"
                      radius="sm"
                      color={VERB_COLORS[row.verb] ?? "gray"}
                    >
                      {verbLabel(row.verb)}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {targetLabel(row.target_type)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {row.diff_keys && row.diff_keys.length > 0 && (
                      <Group gap={4}>
                        {row.diff_keys.slice(0, 3).map((k) => (
                          <Badge key={k} size="xs" variant="light" radius="sm">
                            {diffFieldLabel(k)}
                          </Badge>
                        ))}
                        {row.diff_keys.length > 3 && (
                          <Text size="xs" c="dimmed">
                            +{row.diff_keys.length - 3}
                          </Text>
                        )}
                      </Group>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed" ff="monospace">
                      {row.ip_address ?? "—"}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>

      {totalPages > 1 && (
        <Group justify="center">
          <Pagination
            total={totalPages}
            value={page}
            onChange={setPage}
            size="sm"
            radius="md"
          />
        </Group>
      )}
    </Stack>
  );
}
